<script setup lang="ts">
interface Subcategory {
  id: string
  slug: string
  name: string
}

import { getCategoryPermalink } from '~/lib/'
import type { Category } from '~/types/shopware'
const { apiClient } = useShopwareContext()
const localePath = useLocalePath()
const props = defineProps<{
  category: Category
}>()

const { data: children } = await useAsyncData('cmsNavigationChildren' + props.category.id, async () => {
  const response = await apiClient.invoke('readCategoryList post /category', {
    body: {
      filter: [
        {
          type: 'equals',
          field: 'active',
          value: true as unknown as string,
        },
        {
          type: 'equals',
          field: 'parentId',
          value: props.category.id,
        },
      ],
      sort: [
        {
          field: 'afterCategoryId',
          order: 'ASC',
        },
      ],
      associations: {
        seoUrls: {},
      },
    },
  })

  if (!response.data?.elements) return []
  const elements = response.data.elements as unknown as Category[]

  let child = elements.shift()
  if (!child) return []

  const children = new Map<string, Category>(elements.map((obj) => [obj.afterCategoryId as string, obj]))
  const orderedChildren: Subcategory[] = []

  // Ordering in Shopware is done by chaining elements so we need to do some computation to order them.
  orderedChildren.push({
    id: child.id,
    slug: localePath(getCategoryPermalink(child)),
    name: child.translated.name,
  })
  while (children.size) {
    const nextKey = child.id
    if (!nextKey) break

    child = children.get(nextKey)
    if (!child) break
    children.delete(nextKey)
    orderedChildren.push({
      id: child.id,
      slug: localePath(getCategoryPermalink(child)),
      name: child.translated.name,
    })
  }

  return orderedChildren
})

const subcategories =
  children.value?.map((child) => ({
    id: child.id,
    link: {
      text: child.name,
      url: child.slug,
    },
  })) || []
</script>
<template>
  <div class="subcategories">
    <LinkList :items="subcategories" />
  </div>
</template>
<style scoped lang="scss">
.subcategories :deep(.link-list) {
  display: grid;
  gap: 0 var(--gap);
  @include breakpoint(ts) {
    grid-template-columns: 1fr 1fr;
  }
  .link {
    color: var(--c-primary);
    font-weight: 400;
  }
}
</style>
